import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { take } from 'rxjs';
import { BudgetOptions } from 'src/app/core/constants-repository/project-calculation.constants';
import { ProjectModel } from '../../../../../../../core/models/project/project.model';
import { BUDGET_LABELS } from '../../../../../../../core/text-db/project-calculation.text';
import { WINDOW } from '../../../../../../../features/calendar/util/browser-window-ref';
import { DateTimeHelper } from '../../../../../../../features/calendar/util/date-time-helper';
import { DropdownOption } from '../../../../../../../shared/data-types/dropdown-option';
import { recordToDropdownOptions } from '../../../../../../../shared/functions/dropdown-functions';
import { getEnumKeyByEnumValue } from '../../../../../../../shared/functions/enum-functions';
import { ProjectDataService } from '../../../../state-and-data-handling/project-data.service';

@Component({
  selector: 'app-project-calculation-form-budget',
  templateUrl: './project-calculation-form-budget.component.html',
  styleUrls: ['./project-calculation-form-budget.component.scss'],
  standalone: false,
})
export class ProjectCalculationFormBudgetComponent implements OnInit, OnChanges {
  @Input() toggleHidden: boolean;
  @Output() readonly selectedBudgetOptionChanged = new EventEmitter();
  public budgetForm: FormGroup;
  public budgetOptions: DropdownOption[];
  public selectedBudgetOption: BudgetOptions;
  public readonly BudgetOptions = BudgetOptions;
  private currentlyHidden = false;

  // DI
  private readonly window = inject<Window>(WINDOW);

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly projectDataService: ProjectDataService,
  ) {}

  ngOnInit(): void {
    const projectClone = this.projectDataService.getProjectClone();
    this.selectedBudgetOption = projectClone.budgetType;
    this.initEvents(projectClone);
  }

  ngOnChanges(): void {
    if (!this.toggleHidden && this.toggleHidden !== this.currentlyHidden && this.budgetForm) {
      const option = this.budgetOptions[0];
      this.budgetForm.controls.budgetType.patchValue(option);
      this.window.setTimeout(() => {
        this.budgetOptionChanged(option);
      });
    }
    this.currentlyHidden = this.toggleHidden;
  }

  // The item of the budget rate option combobox has changed
  public budgetOptionChanged($event: DropdownOption): void {
    const enumOption = getEnumKeyByEnumValue(BudgetOptions, $event.value.toString());
    this.budgetForm.controls.budgetValue.patchValue(0);
    this.selectedBudgetOption = BudgetOptions[enumOption];

    const clonedProject = this.projectDataService.getProjectClone();
    clonedProject.projectBudgetEntries = [];

    if (this.selectedBudgetOption === BudgetOptions.TotalProjectMonetaryBudget) {
      clonedProject.projectBudgetEntries = [{ monetary: 0, seconds: null }];
    } else if (this.selectedBudgetOption === BudgetOptions.TotalProjectHours) {
      // Total project hours
      clonedProject.projectBudgetEntries = [{ monetary: null, seconds: 0 }];
    }

    clonedProject.budgetType = this.selectedBudgetOption;
    this.projectDataService.updateProject(clonedProject);
    this.selectedBudgetOptionChanged.next(this.selectedBudgetOption);
  }

  public budgetValueChanged(value: string): void {
    const projectClone = this.projectDataService.getProjectClone();
    if (this.selectedBudgetOption === BudgetOptions.TotalProjectMonetaryBudget) {
      const projectFees = +value;
      projectClone.projectBudgetEntries = [{ monetary: projectFees, seconds: null }];
    } else if (this.selectedBudgetOption === BudgetOptions.TotalProjectHours) {
      const projectSeconds = DateTimeHelper.hoursToSeconds(+value);
      projectClone.projectBudgetEntries = [{ monetary: null, seconds: projectSeconds }];
    }

    this.projectDataService.updateProject(projectClone);
    this.projectDataService.userOperationConducted$.next(true);
  }

  private initEvents(project: ProjectModel): void {
    recordToDropdownOptions(BUDGET_LABELS)
      .pipe(take(1))
      .subscribe((budgetOptions) => {
        this.budgetOptions = budgetOptions;
        const selectedOption = budgetOptions.find((option) => option.value === project.budgetType);

        let budget = 0;
        if (this.selectedBudgetOption === BudgetOptions.TotalProjectMonetaryBudget) {
          budget = project.projectBudgetEntries[0].monetary;
        } else if (this.selectedBudgetOption === BudgetOptions.TotalProjectHours) {
          budget = DateTimeHelper.secondsToHours(project.projectBudgetEntries[0].seconds);
        }

        this.budgetForm = this.formBuilder.group({
          budgetType: [selectedOption],
          budgetValue: [budget],
        });
      });
  }
}
